import {
  Button,
  Card,
  Form,
  Input,
  Typography,
  Layout,
  Progress,
  message,
} from "antd";
import { LockOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SET_PASSWORD } from "../../services/apiEndPoints";

const { Title, Text } = Typography;
const { Content } = Layout;

function getPasswordStrength(password: string) {
  let score = 0;

  if (!password) return { score: 0, label: "Weak", color: "#ff4d4f" };

  if (password.length >= 8) score += 25;
  if (/[A-Z]/.test(password)) score += 25;
  if (/[0-9]/.test(password)) score += 25;
  if (/[^A-Za-z0-9]/.test(password)) score += 25;

  if (score <= 25) return { score, label: "Weak", color: "#ff4d4f" };
  if (score <= 50) return { score, label: "Fair", color: "#faad14" };
  if (score <= 75) return { score, label: "Good", color: "#1890ff" };

  return { score, label: "Strong", color: "#52c41a" };
}

export default function SetPasswordPage() {
  const [password, setPassword] = useState("");
  const strength = getPasswordStrength(password);
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token");
  const memberId = searchParams.get("memberId");

  const handleSubmit = async () => {
    if (!token || !memberId || !password) {
      console.error("Missing token or memberId");
      return;
    }

    if (strength.score < 50) {
      console.error("Password strength is insufficient");
      return;
    }

    await fetch(SET_PASSWORD, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        memberId,
        token,
        password,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to set password");
        }
        message.success("Password has been set successfully");
        setTimeout(() => {
          window.location.href = "/login";
        }, 1500);
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };

  return (
    <Layout className="login-layout">
      {/* Decorative Side */}
      <div className="login-decor">
        <div className="decor-circle decor-1" />
        <div className="decor-circle decor-2" />

        <div className="decor-content">
          <Title level={2} style={{ color: "#fff" }}>
            Set New Password 🔑
          </Title>
          <Text style={{ color: "#e5e7eb" }}>
            Create a strong password to secure your account
          </Text>
        </div>
      </div>

      {/* Set Password Form */}
      <Content className="login-content">
        <Card className="login-card">
          <Title level={3}>Set Password</Title>
          <Text type="secondary">
            Please enter and confirm your new password below.
          </Text>

          <Form layout="vertical" style={{ marginTop: 24 }}>
            <Form.Item
              label="New Password"
              name="password"
              rules={[
                { required: true, message: "Enter your new password" },
                { min: 8, message: "Password must be at least 8 characters" },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Enter new password"
                onChange={(e) => setPassword(e.target.value)}
              />
            </Form.Item>

            {/* Password Strength Meter */}
            {password && (
              <div style={{ marginBottom: 16 }}>
                <Progress
                  percent={strength.score}
                  showInfo={false}
                  strokeColor={strength.color}
                />
                <Text style={{ color: strength.color, fontSize: 12 }}>
                  Strength: {strength.label}
                </Text>
              </div>
            )}

            <Form.Item
              label="Confirm Password"
              name="confirmPassword"
              dependencies={["password"]}
              rules={[
                { required: true, message: "Confirm your password" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Passwords do not match"));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Confirm new password"
              />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              onClick={() => {
                handleSubmit();
              }}
            >
              Set Password
            </Button>

            <Link to="/login">
              <Button
                type="link"
                block
                icon={<ArrowLeftOutlined />}
                style={{ marginTop: 12 }}
              >
                Back to Login
              </Button>
            </Link>

            <Text type="secondary" style={{ display: "block", marginTop: 16 }}>
              © 2025 Access Warehousing
            </Text>
          </Form>
        </Card>
      </Content>
    </Layout>
  );
}
